import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiUrl } from "../../config/api";
import "./OlivaSales.css";

const sessionHeaders = (json = false) => {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  return {
    Authorization: session.sessionKey || "",
    Username: session.username || "",
    ...(json ? { "Content-Type": "application/json" } : {}),
  };
};

const today = () => new Date().toLocaleDateString("en-CA");
const money = (value) => `$${Number(value || 0).toLocaleString("es-CL")}`;

const emptySale = () => ({
  isCustom: false,
  serviceId: "",
  serviceName: "",
  customerName: "",
  serviceDate: today(),
  totalAmount: "",
  discount: "0",
  paymentMethod: "transfer",
  paymentStatus: "paid",
  responsibleUsername: JSON.parse(localStorage.getItem("sessionData") || "{}").username || "",
  location: "",
  notes: "",
});

function OlivaSales() {
  const [tab, setTab] = useState("sales");
  const [services, setServices] = useState([]);
  const [sales, setSales] = useState([]);
  const [locations, setLocations] = useState([]);
  const [sale, setSale] = useState(emptySale);
  const [serviceDraft, setServiceDraft] = useState({ name: "", description: "", defaultPrice: "" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const saleAttemptRef = useRef(null);
  const saleDraftRef = useRef(sale);
  saleDraftRef.current = sale;
  const saleSendingRef = useRef(false);
  const [pendingSale, setPendingSale] = useState(null);


  const selectedService = useMemo(
    () => services.find((item) => String(item.id) === String(sale.serviceId)),
    [services, sale.serviceId]
  );

  async function load() {
    setLoading(true);
    setMessage("");
    try {
      const [servicesResponse, salesResponse, locationsResponse] = await Promise.all([
        fetch(apiUrl("/oliva/sales/services"), { headers: sessionHeaders() }),
        fetch(apiUrl("/oliva/sales/sales"), { headers: sessionHeaders() }),
        fetch(apiUrl("/locations/all?excludeEcommerce=true"), { headers: sessionHeaders() }),
      ]);
      const [servicesData, salesData, locationsData] = await Promise.all([
        servicesResponse.json(), salesResponse.json(), locationsResponse.json(),
      ]);
      if (!servicesData.success) throw new Error(servicesData.message);
      if (!salesData.success) throw new Error(salesData.message);
      setServices(servicesData.services || []);
      setSales(salesData.sales || []);
      setLocations(locationsData.locations || []);
    } catch (error) {
      setMessage(error.message || "No se pudo cargar ventas de servicios.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function selectService(value) {
    const service = services.find((item) => String(item.id) === value);
    setSale((current) => ({
      ...current,
      serviceId: value,
      totalAmount: service ? String(service.default_price) : current.totalAmount,
    }));
  }

  async function registerSale(event, recovery = false) {
    event?.preventDefault();
    if (saleSendingRef.current) return;
    const username = sessionHeaders().Username;
    const storageKey = `ovm_oliva_sale_attempt_${username}`;
    const payload = {
      ...sale, serviceId: sale.isCustom ? null : Number(sale.serviceId),
      totalAmount: Number(sale.totalAmount), discount: Number(sale.discount || 0),
      location: sale.location ? Number(sale.location) : null,
    };
    const fingerprint = JSON.stringify(payload);
    const submittedDraft = sale;
    saleSendingRef.current = true;
    setBusy(true);
    setMessage("");
    try {
      let attempt = saleAttemptRef.current;
      if (!attempt) attempt = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (attempt && attempt.fingerprint !== fingerprint && !recovery) {
        setPendingSale(attempt);
        setMessage('La venta anterior sigue sin confirmar. Verifícala antes de registrar otra.');
        return;
      }
      if (!attempt) attempt = { key: `oliva-sale-${window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`}`, fingerprint, payload };
      // Persist BEFORE dispatch. If storage is unavailable, no sale is submitted, so a
      // reload cannot lose an accepted operation and turn its retry into a second sale.
      localStorage.setItem(storageKey, JSON.stringify(attempt));
      saleAttemptRef.current = attempt;
      const response = await fetch(apiUrl("/oliva/sales/sales"), {
        method: "POST", headers: sessionHeaders(true),
        body: JSON.stringify({ ...attempt.payload, operationKey: attempt.key }),
      });
      const data = await response.json();
      if (!data.success) {
        if ([400,401,403,404].includes(response.status)) {
          localStorage.removeItem(storageKey);
          saleAttemptRef.current = null;
          setPendingSale(null);
        }
        throw new Error(data.message);
      }
      // Clear storage first: a failed removal must preserve recovery rather than silently
      // authorize a new key. The server will replay the row on the next verification.
      localStorage.removeItem(storageKey);
      saleAttemptRef.current = null;
      setPendingSale(null);
      setSales((current) => [data.sale, ...current.filter((row) => String(row.id) !== String(data.sale.id))]);
      if (attempt.fingerprint === fingerprint && saleDraftRef.current === submittedDraft) setSale(emptySale());
      setMessage(data.replayed ? 'Venta ya registrada; se recuperó sin duplicarla.' : 'Venta de servicio registrada.');
    } catch (error) {
      setPendingSale(saleAttemptRef.current);
      setMessage(error.message || "No se pudo registrar la venta.");
    } finally {
      saleSendingRef.current = false;
      setBusy(false);
    }
  }

  async function createService(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch(apiUrl("/oliva/sales/services"), {
        method: "POST",
        headers: sessionHeaders(true),
        body: JSON.stringify({
          ...serviceDraft,
          defaultPrice: Number(serviceDraft.defaultPrice),
        }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message);
      setServices((current) => [...current, data.service].sort((a, b) => a.name.localeCompare(b.name)));
      setServiceDraft({ name: "", description: "", defaultPrice: "" });
      setMessage("Servicio agregado al catálogo.");
    } catch (error) {
      setMessage(error.message || "No se pudo crear el servicio.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="oliva-sales">
      <header className="oliva-sales__header">
        <div>
          <p>Oliva y Miel</p>
          <h1>Ventas de servicios</h1>
          <span>Registra servicios del catálogo o atenciones personalizadas.</span>
        </div>
        <nav aria-label="Secciones de ventas">
          <button type="button" className={tab === "sales" ? "is-active" : ""} onClick={() => setTab("sales")}>Ventas</button>
          <button type="button" className={tab === "catalog" ? "is-active" : ""} onClick={() => setTab("catalog")}>Catálogo</button>
        </nav>
      </header>

      {message && <div className="oliva-sales__message" role="status">{message}</div>}
      {pendingSale && <button type="button" disabled={busy} onClick={() => registerSale(null, true)}>Verificar venta pendiente</button>}
      {loading ? <div className="oliva-sales__empty">Cargando…</div> : tab === "sales" ? (
        <div className="oliva-sales__layout">
          <form className="oliva-sales__card oliva-sales__form" onSubmit={registerSale}>
            <h2>Registrar venta</h2>
            <label className="oliva-sales__check">
              <input type="checkbox" checked={sale.isCustom} onChange={(event) => setSale({ ...sale, isCustom: event.target.checked, serviceId: "", serviceName: "", totalAmount: "" })} />
              Servicio personalizado
            </label>
            {sale.isCustom ? (
              <label>Nombre del servicio<input required value={sale.serviceName} onChange={(event) => setSale({ ...sale, serviceName: event.target.value })} /></label>
            ) : (
              <label>Servicio<select required value={sale.serviceId} onChange={(event) => selectService(event.target.value)}>
                <option value="">Seleccionar…</option>
                {services.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}
              </select></label>
            )}
            {selectedService?.description && !sale.isCustom && <small>{selectedService.description}</small>}
            <div className="oliva-sales__fields">
              <label>Cliente<input value={sale.customerName} onChange={(event) => setSale({ ...sale, customerName: event.target.value })} /></label>
              <label>Fecha<input required type="date" value={sale.serviceDate} onChange={(event) => setSale({ ...sale, serviceDate: event.target.value })} /></label>
              <label>Monto<input required min="0" step="1" type="number" value={sale.totalAmount} onChange={(event) => setSale({ ...sale, totalAmount: event.target.value })} /></label>
              <label>Descuento<input min="0" step="1" type="number" value={sale.discount} onChange={(event) => setSale({ ...sale, discount: event.target.value })} /></label>
              <label>Pago<select value={sale.paymentMethod} onChange={(event) => setSale({ ...sale, paymentMethod: event.target.value })}>
                <option value="cash">Efectivo</option><option value="card">Tarjeta</option><option value="transfer">Transferencia</option><option value="other">Otro</option>
              </select></label>
              <label>Estado<select value={sale.paymentStatus} onChange={(event) => setSale({ ...sale, paymentStatus: event.target.value })}>
                <option value="paid">Pagado</option><option value="partial">Parcial</option><option value="pending">Pendiente</option><option value="cancelled">Cancelado</option>
              </select></label>
              <label>Responsable<input value={sale.responsibleUsername} onChange={(event) => setSale({ ...sale, responsibleUsername: event.target.value })} /></label>
              <label>Ubicación<select value={sale.location} onChange={(event) => setSale({ ...sale, location: event.target.value })}>
                <option value="">Sin ubicación</option>{locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
              </select></label>
            </div>
            <label>Notas<textarea rows="3" value={sale.notes} onChange={(event) => setSale({ ...sale, notes: event.target.value })} /></label>
            <button className="oliva-sales__primary" disabled={busy} type="submit">{busy ? "Guardando…" : "Registrar venta"}</button>
          </form>

          <section className="oliva-sales__card">
            <h2>Ventas recientes</h2>
            {sales.length === 0 ? <div className="oliva-sales__empty">Todavía no hay ventas registradas.</div> : (
              <ul className="oliva-sales__list">
                {sales.map((item) => <li key={item.id}>
                  <div><strong>{item.service_name}</strong><span>{item.customer_name || "Sin cliente"} · {item.service_date}</span></div>
                  <div><strong>{money(item.total_amount)}</strong><span>{item.payment_status === "paid" ? "Pagado" : item.payment_status}</span></div>
                </li>)}
              </ul>
            )}
          </section>
        </div>
      ) : (
        <div className="oliva-sales__layout">
          <form className="oliva-sales__card oliva-sales__form" onSubmit={createService}>
            <h2>Nuevo servicio</h2>
            <label>Nombre<input required value={serviceDraft.name} onChange={(event) => setServiceDraft({ ...serviceDraft, name: event.target.value })} /></label>
            <label>Precio predeterminado<input required type="number" min="0" step="1" value={serviceDraft.defaultPrice} onChange={(event) => setServiceDraft({ ...serviceDraft, defaultPrice: event.target.value })} /></label>
            <label>Descripción<textarea rows="4" value={serviceDraft.description} onChange={(event) => setServiceDraft({ ...serviceDraft, description: event.target.value })} /></label>
            <button className="oliva-sales__primary" disabled={busy} type="submit">Agregar al catálogo</button>
          </form>
          <section className="oliva-sales__card">
            <h2>Catálogo</h2>
            <ul className="oliva-sales__list">{services.map((service) => <li key={service.id}><div><strong>{service.name}</strong><span>{service.description || "Sin descripción"}</span></div><strong>{money(service.default_price)}</strong></li>)}</ul>
          </section>
        </div>
      )}
    </main>
  );
}

export default OlivaSales;
